import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@aions/ui-primitives';
import { stripExif, isHeicFile, convertHeicToJpeg } from '../../lib/exif-strip';

interface Props {
  files: File[];
  onFilesChange: (files: File[]) => void;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

export function Step3Anexos({ files, onFilesChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragover, setDragover] = useState(false);
  const [stripping, setStripping] = useState(false);
  const [heicDropped, setHeicDropped] = useState(false);

  const addFiles = useCallback(
    async (newFiles: FileList | File[]) => {
      setStripping(true);
      setHeicDropped(false);
      try {
        // Remove EXIF/GPS ANTES do arquivo sair do wizard. Defesa em profundidade:
        // mesmo se o encrypt for contornado, o arquivo já não tem localização
        // nem identificadores de dispositivo (T-EXIF, INT-04).
        const incoming = Array.from(newFiles).filter((f) => f.size <= MAX_FILE_SIZE);
        let dropped = false;
        const processed = await Promise.all(
          incoming.map(async (f): Promise<File | null> => {
            // HEIC/HEIF: converter → JPEG no canvas (pixels only → sem metadados).
            // Onde o browser não decodifica HEIC, dropa (nunca passa sem conversão).
            if (await isHeicFile(f)) {
              try {
                return await convertHeicToJpeg(f);
              } catch {
                dropped = true;
                return null;
              }
            }
            return stripExif(f).catch(() => f); // JPEG malformado cai no original
          }),
        );
        const kept = processed.filter((f): f is File => f !== null);
        if (dropped) setHeicDropped(true);
        if (kept.length > 0) onFilesChange([...files, ...kept]);
      } finally {
        setStripping(false);
      }
    },
    [files, onFilesChange],
  );

  const removeFile = useCallback(
    (index: number) => {
      onFilesChange(files.filter((_, i) => i !== index));
    },
    [files, onFilesChange],
  );

  return (
    <div className="fade-in">
      <h2>{t('wizard.step3_title')}</h2>
      <p style={{ color: 'var(--color-text-secondary)', margin: '8px 0 16px' }}>
        {t('step3.instruction')}
      </p>

      <div
        className={`dropzone ${dragover ? 'dragover' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragover(true);
        }}
        onDragLeave={() => setDragover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragover(false);
          if (e.dataTransfer.files) void addFiles(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        aria-label={t('step3.drag_drop')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.click();
        }}
      >
        <p style={{ fontSize: '2rem', marginBottom: 8 }}>📎</p>
        <p>{t('step3.drag_drop')}</p>
        <p style={{ fontSize: '0.8125rem', marginTop: 4 }}>
          {t('step3.max_size')} · {t('step3.supported_types')}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) void addFiles(e.target.files);
        }}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
      />

      <div className="alert alert-info" style={{ marginTop: 16 }}>
        {t('step3.exif_note')}
      </div>

      <details className="big-files">
        <summary className="big-files__summary">{t('step3.big_files_summary')}</summary>
        <div className="big-files__body">
          <p className="big-files__intro">{t('step3.big_files_intro')}</p>
          <ol className="big-files__steps">
            <li>{t('step3.big_files_step1')}</li>
            <li>{t('step3.big_files_step2')}</li>
            <li>{t('step3.big_files_step3')}</li>
          </ol>
          <Alert variant="warning" title={t('step3.big_files_warning_title')}>
            <strong>{t('step3.big_files_warning_lead')}</strong> {t('step3.big_files_warning_rest')}
          </Alert>
        </div>
      </details>

      {stripping && (
        <p
          style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: 8 }}
          role="status"
          aria-live="polite"
        >
          {t('step3.stripping')}
        </p>
      )}

      {heicDropped && (
        <Alert variant="warning" role="alert" style={{ marginTop: 12 }} data-testid="heic-dropped">
          {t('step3.heic_failed')}
        </Alert>
      )}

      {files.length > 0 && (
        <div className="file-list">
          <p
            style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 8 }}
          >
            {t('step3.file_added', { count: files.length })}
          </p>
          {files.map((f, i) => (
            <div key={`${f.name}-${String(i)}`} className="file-item">
              <span>
                {f.name} ({formatSize(f.size)})
              </span>
              <button
                className="file-remove"
                onClick={() => removeFile(i)}
                aria-label={`Remover ${f.name}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)}B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))}KB`;
  return `${String((bytes / (1024 * 1024)).toFixed(1))}MB`;
}
